// apps/admin-ui/src/components/widgets/ReserveVerification.tsx
// Reserve verification widget for CPA auditors and custodians
// ALL RIGHTS RESERVED

import '../styles/widgets.css';

export interface ReserveVerificationProps {
  data?: {
    lastVerified: string;
    verifiedBy: string;
    holdings: {
      type: string;
      amount: number;
      custodian: string;
      verified: boolean;
    }[];
    totalVerified: number;
    totalUnverified: number;
  };
}

export function ReserveVerification({ data }: ReserveVerificationProps) {
  // Mock data for development
  const verification = data || {
    lastVerified: '2026-02-01T10:30:00Z',
    verifiedBy: 'BNY Mellon',
    holdings: [
      { type: 'Cash (USD)', amount: 56_250_000, custodian: 'BNY Mellon', verified: true },
      { type: 'T-Bills (90-day)', amount: 37_500_000, custodian: 'BNY Mellon', verified: true },
      { type: 'Money Market', amount: 18_750_000, custodian: 'State Street', verified: true },
      { type: 'Real Estate Fund', amount: 12_500_000, custodian: 'Pending', verified: false },
    ],
    totalVerified: 112_500_000,
    totalUnverified: 12_500_000,
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });

  const verifiedPct = (verification.totalVerified / (verification.totalVerified + verification.totalUnverified)) * 100;

  return (
    <div className="widget-verification">
      <div className="verification-header">
        <span className="verification-time">
          Last verified: {formatDateTime(verification.lastVerified)}
        </span>
        <span className="verification-by">
          by {verification.verifiedBy}
        </span>
      </div>

      <div className="verification-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${verifiedPct}%` }}
          />
        </div>
        <div className="progress-labels">
          <span className="verified">
            ✓ Verified: {formatCurrency(verification.totalVerified)}
          </span>
          <span className="unverified">
            ◇ Pending: {formatCurrency(verification.totalUnverified)}
          </span>
        </div>
      </div>

      <table className="verification-table">
        <thead>
          <tr>
            <th>Asset Type</th>
            <th>Amount</th>
            <th>Custodian</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {verification.holdings.map((holding, idx) => (
            <tr key={idx}>
              <td>{holding.type}</td>
              <td className="amount">{formatCurrency(holding.amount)}</td>
              <td>{holding.custodian}</td>
              <td className={holding.verified ? 'verified' : 'pending'}>
                {holding.verified ? '✓' : '◇'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ReserveVerification;
